import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, DotsThreeVertical } from "@phosphor-icons/react";
import { toast } from "sonner";
import CustomerLink from "@/components/CustomerLink";

const STATUS = ["New", "Assigned", "In Progress", "Waiting", "Resolved", "Closed"];
const CATEGORIES = ["Service Delay", "Wrong Document", "Payment Issue", "Application Rejected", "Staff Behaviour", "Other"];
const PRIORITY = ["Low", "Medium", "High", "Urgent"];

const STATUS_TONE = {
  New: "bg-slate-100 text-slate-700 border-slate-200",
  Assigned: "bg-blue-50 text-blue-700 border-blue-200",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-200",
  Waiting: "bg-purple-50 text-purple-700 border-purple-200",
  Resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Closed: "bg-slate-800 text-white border-slate-800",
};

const PRIORITY_TONE = {
  Low: "text-slate-600 border-slate-300",
  Medium: "text-blue-700 border-blue-300 bg-blue-50",
  High: "text-orange-700 border-orange-300 bg-orange-50",
  Urgent: "text-red-700 border-red-300 bg-red-50",
};

export default function Complaints() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ customer_id: "", service_id: "", category: "", priority: "Medium", description: "" });
  const [resolution, setResolution] = useState("");

  const load = async () => setRows((await api.get("/complaints")).data);

  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/services").then((r) => setServices(r.data));
  }, []);

  const save = async () => {
    if (!form.customer_id || !form.category || !form.description) { toast.error("Fill required fields"); return; }
    try {
      await api.post("/complaints", form);
      toast.success("Complaint registered");
      setOpen(false); setForm({ customer_id: "", service_id: "", category: "", priority: "Medium", description: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (id, status) => {
    try { await api.patch(`/complaints/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveResolution = async () => {
    try {
      await api.patch(`/complaints/${detail.id}`, { resolution, status: "Resolved" });
      toast.success("Resolution saved");
      setDetail(null); setResolution(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">Support</div>
          <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Complaints</h1>
          <p className="text-sm text-slate-600 mt-2">Track customer grievances from filing to resolution.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-complaint-btn" className="bg-orange-600 hover:bg-orange-700 text-white h-11 gap-2 hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,background-color] duration-200">
              <Plus size={18} weight="bold" /> New Complaint
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Register Complaint</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <F label="Customer *">
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="cmp-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent className="max-h-72">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.customer_no} · {c.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Related Service (optional)">
                <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="max-h-72">{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Category *">
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="cmp-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Priority">
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITY.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
              </div>
              <F label="Description *">
                <Textarea data-testid="cmp-desc-input" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </F>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="cmp-save-btn" onClick={save} className="bg-orange-600 hover:bg-orange-700 text-white">Register</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-white border-slate-200 overflow-hidden" data-testid="complaints-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Ref #</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Customer</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Category</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Priority</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-16">No complaints registered.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} className="hover:bg-slate-50">
                <TableCell className="font-mono text-xs">{r.complaint_no}</TableCell>
                <TableCell><CustomerLink id={r.customer_id} name={r.customer_name} subline={r.service_name} /></TableCell>
                <TableCell className="text-sm">{r.category}</TableCell>
                <TableCell><Badge variant="outline" className={`${PRIORITY_TONE[r.priority]} font-medium`}>{r.priority}</Badge></TableCell>
                <TableCell><Badge variant="outline" className={`${STATUS_TONE[r.status]} font-medium text-[10px] uppercase tracking-wider`}>{r.status}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`cmp-menu-${r.id}`}><DotsThreeVertical size={18} /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-white" align="end">
                      <DropdownMenuItem onClick={() => { setDetail(r); setResolution(r.resolution || ""); }}>View / Resolve</DropdownMenuItem>
                      {STATUS.map((s) => (
                        <DropdownMenuItem key={s} onClick={() => changeStatus(r.id, s)} disabled={r.status === s}>
                          Mark {s}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && (setDetail(null), setResolution(""))}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Complaint Details</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 py-2">
              <div className="text-sm"><span className="font-mono text-slate-500">{detail.complaint_no}</span> · <Badge variant="outline" className={STATUS_TONE[detail.status]}>{detail.status}</Badge></div>
              <div className="text-sm"><span className="text-slate-500">Customer:</span> <b>{detail.customer_name}</b></div>
              <div className="text-sm"><span className="text-slate-500">Category:</span> {detail.category} · <span className="text-slate-500">Priority:</span> {detail.priority}</div>
              <div className="text-sm bg-slate-50 border border-slate-200 p-3 rounded">{detail.description}</div>
              <F label="Resolution / Action Taken">
                <Textarea rows={3} data-testid="cmp-resolution-input" value={resolution} onChange={(e) => setResolution(e.target.value)} />
              </F>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => (setDetail(null), setResolution(""))}>Close</Button>
            <Button data-testid="cmp-resolve-btn" onClick={saveResolution} className="bg-emerald-600 hover:bg-emerald-700 text-white">Save & Mark Resolved</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
